import { useEffect } from 'react';
import { Link } from 'react-router';
import { Logo } from '../ui/Logo';
import { useSiteTheme } from '../lib/palette';

const UPDATED = 'November 3, 2026';

/**
 * The public privacy policy (#288, updated for billing in #567), at /privacy for anyone, signed in or not. The
 * browser-extension stores link to it, so it must stay accurate to what the app, the desktop apps and the extension
 * actually collect.
 */
export function PrivacyPolicy() {
  // Opened from the home page footer, start at the top rather than where that page was scrolled.
  useEffect(() => window.scrollTo(0, 0), []);
  useSiteTheme(); // the public site's look (#719)
  return (
    <div className="legal-page">
      <header className="legal-head">
        <Link to="/" aria-label="ClassGuardian home">
          <Logo />
        </Link>
      </header>
      <main className="legal-body">
        <h1>Privacy Policy</h1>
        <p className="muted">Last updated {UPDATED}</p>

        <p>
          ClassGuardian helps homeschool families plan school, keep records, and supervise a student’s computer during the school hours a parent sets.
          ClassGuardian is operated by Vieira Ventures LLC (“we”, “us”). This policy explains what information ClassGuardian collects, why, who it’s shared
          with, and what control you have over it. It covers the website and web app, the ClassGuardian desktop app for Windows and Mac, and the
          ClassGuardian browser extension. Our <Link to="/terms">Terms of Service</Link> explain the rules for using ClassGuardian.
        </p>

        <h2>The short version</h2>
        <ul>
          <li>We collect only what’s needed for a parent to run their homeschool and supervise school time.</li>
          <li>Computer and website activity is recorded <strong>only during the school hours the parent sets</strong>, never outside them.</li>
          <li>
            Live view, camera and microphone are never recorded, and the student’s computer shows when a parent is watching, unless the parent turns on
            Hidden mode, or Ghost mode for the screen (the camera and microphone always show the banner).
          </li>
          <li>Your data is never sold, never used for advertising, and never shared with third parties for their own purposes.</li>
          <li>Card payments are handled by Stripe. We never see or store your card number.</li>
          <li>A parent can see, export and delete their family’s information at any time.</li>
        </ul>

        <h2>Who controls the information</h2>
        <p>
          A parent creates a ClassGuardian account and adds their children. The parent decides what is supervised, when school time runs, and who in
          the family can see what. Children use ClassGuardian only through an account their parent controls. ClassGuardian is a tool for parents. It
          is not directed at children independently, and we don’t use children’s information for any purpose other than providing the service to their
          parent.
        </p>

        <h2>What we collect</h2>
        <h3>Information you give us</h3>
        <ul>
          <li>
            <strong>Parent account:</strong> your name, email address, password (stored only as a secure hash, never readable), time zone, and settings
            such as notification and theme choices. If you turn on two-factor sign-in, its secret is stored encrypted; passkeys store only a public key.
          </li>
          <li>
            <strong>Family:</strong> the family name, the adults you invite and what each may see and do.
          </li>
          <li>
            <strong>Children’s profiles:</strong> the name or nickname you enter, and optionally age, grade, color, and subjects, and which subjects count
            as core. If you turn on the Student Portal, the student’s sign-in code and a password hash.
          </li>
          <li>
            <strong>School records you enter:</strong> schedules, school calendars, planner items and assignments, lesson plans, grades, attendance
            hours (and how a day’s hours split by subject), reading logs, journal entries, report comments, and the parents’ to-do list.
          </li>
          <li>
            <strong>Your state’s requirements:</strong> the state and homeschool option you pick, any day or hour target you enter, and which filing
            deadlines you mark filed or move.
          </li>
          <li>
            <strong>State compliance, only if you turn it on:</strong> state compliance is off until your family’s owner turns it on and agrees to what
            it keeps. While it’s on we keep each child’s full legal name and date of birth (on their profile, where their grade is then required too),
            the primary parent’s legal name, home address and phone (on their account), the name and address of your school district’s official, and
            curriculum notes. We use them only to fill in the state documents you make (notices of intent, instruction plans, progress reports and
            transcripts). They’re encrypted in our database, seen only by you and the adults you share each child with, never by your child, and never
            sold or shared. We keep a record of when you agreed. Turning state compliance off deletes them straight away, and, if you choose, the
            documents ClassGuardian made from them.
          </li>
          <li>
            <strong>Work samples, tests and evaluations:</strong> photos and PDFs of your child’s work (your student can add them when turning work in, if
            you allow it), test scores and evaluators’ findings, and the scanned reports you attach.
          </li>
          <li>
            <strong>Messages:</strong> chat messages, and help and “done for today” requests between a parent and their student.
          </li>
          <li>
            <strong>Help &amp; Feedback:</strong> the requests, messages and attachments you send us.
          </li>
        </ul>

        <h3>Information collected during school time</h3>
        <p>When a parent pairs a student’s computer or browser, ClassGuardian collects the following, only during the parent’s scheduled school hours:</p>
        <ul>
          <li>
            <strong>Website activity:</strong> the address (URL) and title of the web pages the student opens, and when. This lets the parent see what was
            used for school and get alerts when the student leaves the approved learning sites.
          </li>
          <li>
            <strong>App activity</strong> (desktop app): the names of the apps in use and roughly how long, in five-minute steps, and when an app the parent
            blocked is closed.
          </li>
          <li>
            <strong>Blocked attempts:</strong> when the student tries to open a site or app the parent has blocked.
          </li>
          <li>
            <strong>Attendance estimate:</strong> a daily estimate of school hours worked out from the activity above.
          </li>
          <li>
            <strong>Device status:</strong> whether the paired device is online, the app version, and the device name, so the parent knows supervision is
            working.
          </li>
        </ul>
        <p>
          <strong>Live view</strong> (seeing the student’s screen, and, if the parent turns them on, the student’s camera and microphone),{' '}
          <strong>talking</strong>, the <strong>pointer</strong> and <strong>remote control</strong> happen only when a parent starts them during
          school time. They are streamed in real time and are <strong>not recorded or saved</strong>. The <strong>whiteboard</strong>’s drawings are
          kept so the board looks the same on every screen, and start fresh after 12 hours without use. The student’s computer shows a banner while a
          parent is watching or talking, unless the parent turns on Hidden mode (which hides ClassGuardian on the student’s computer) or Ghost mode (which hides the banner while a parent watches the screen only; the camera and microphone always show it). On a Mac, the student’s computer asks for permission to share the screen, camera
          and microphone before any of these work.
        </p>

        <h3>Information about how you use ClassGuardian</h3>
        <ul>
          <li>
            <strong>Sign-ins:</strong> the time, the account, whether it succeeded, the IP address and the browser or device, so you and we can spot
            someone else trying to get in. Kept for one year.
          </li>
          <li>
            <strong>Email we send:</strong> a count of emails by type, for running the service. We don’t keep copies of the emails.
          </li>
          <li>
            <strong>Browser notifications:</strong> if you turn them on, the push address your browser gives us to deliver them.
          </li>
        </ul>

        <h3>Payments</h3>
        <p>
          When you subscribe, you pay on a secure page run by our payment processor, <strong>Stripe</strong>. Stripe collects your card details and billing
          address; we never see or store your card number. We keep the Stripe customer and subscription identifiers, your plan, billing period, renewal
          date, payment status, and any credits, so we can turn your plan’s features on and off. Stripe’s use of your information is covered by{' '}
          <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
            Stripe’s privacy policy
          </a>
          .
        </p>
        <p>
          <strong>Referrals:</strong> if you sign up through a friend’s link, we note which family referred you so we can give you both your free month.
          The referring family sees only how many families joined with their link, not who.
        </p>

        <h3>What we don’t collect</h3>
        <ul>
          <li>Nothing outside the school hours the parent sets, or while the student is on an approved break or done for the day.</li>
          <li>No keystrokes, passwords typed on websites, or the contents of web pages.</li>
          <li>No recordings of the screen, camera or microphone.</li>
          <li>No precise location, contacts, or health information, and no payment card numbers.</li>
          <li>No advertising identifiers or tracking for advertising.</li>
        </ul>

        <h2>The browser extension</h2>
        <p>
          The ClassGuardian extension’s single purpose is to log, and if the parent chooses, block websites during the student’s school hours, and to
          report that activity to the parent. It works only after a parent pairs it with their ClassGuardian account. It stores the pairing details and
          the current school-time rules in the browser. During school time it sends the address, title and time of the pages the student opens to the
          family’s ClassGuardian server. It does not read page contents, it does nothing outside school hours, and it runs no code downloaded from the
          internet.
        </p>

        <h2>How we use information</h2>
        <ul>
          <li>To provide ClassGuardian to the parent: show activity, reports, grades and records, and enforce the parent’s website and app rules.</li>
          <li>To send the emails and notifications the parent turns on, such as alerts and the weekly summary, and account emails such as invites and password resets.</li>
          <li>
            To remind the parent, by email and on their devices, before their state’s filing deadlines (they can turn these reminders off in Settings →
            Notifications), and to make the documents and portfolio they ask for.
          </li>
          <li>To bill for subscriptions, apply credits, and turn plan features on or off.</li>
          <li>To answer Help &amp; Feedback requests.</li>
          <li>To keep accounts secure, prevent abuse, and fix problems.</li>
        </ul>
        <p>We don’t sell your information, use it for advertising, or use it to decide creditworthiness or for lending.</p>

        <h2>Who we share it with</h2>
        <p>Information is shared only with:</p>
        <ul>
          <li>
            <strong>The people in your family you choose</strong>, such as a co-parent or tutor you invite, with the access you give them, and your student
            seeing their own planner, grades and messages.
          </li>
          <li>
            <strong>People you share an evaluator link with.</strong> If you make a link on the Portfolio tab, anyone who has it can see the parts of
            one child’s school year you chose (for example attendance, grades, work samples and tests), read-only, until it expires or you turn it off.
            They don’t need an account. We count when it’s opened and tell you the first time. If you ask, we email the link to the address you give.
          </li>
          <li>
            <strong>Service providers</strong> that help us run ClassGuardian and may use it only to provide that service to us: hosting (DigitalOcean, in
            the United States), email delivery (Resend), payments and sales tax (Stripe), and, for support requests we need to pass to our developers,
            our issue tracker (GitHub). We remove what isn’t needed before passing a request on.
          </li>
          <li>
            <strong>Authorities</strong>, if the law requires it, or to protect someone’s safety.
          </li>
        </ul>
        <p>We never sell it or share it with third parties for their own purposes.</p>

        <h2>How long we keep it</h2>
        <ul>
          <li>
            <strong>Website and app activity, and messages,</strong> are automatically deleted after <strong>90 days</strong>.
          </li>
          <li>
            <strong>School records</strong> you enter (grades, attendance, planner, lesson plans, reading logs, journal, work samples, tests and
            evaluations, and, while state compliance is on, its details) are kept while you have a plan, because they are your records. If you cancel (or a free
            trial ends without subscribing), they are kept for <strong>45 days</strong> after your access ends so you can download them, and then deleted;
            we email you a week before. A server’s operator may set
            a limit on how long work samples are kept; if so, you get an email a month before any are removed so you can download your portfolio first.
          </li>
          <li>
            <strong>Evaluator links</strong> stop working when they expire or you turn them off. A record of each link (who it was for, when it was
            opened) stays with the child’s records until you delete the child or your account.
          </li>
          <li>
            <strong>Billing records</strong> (invoices and payments) are kept by Stripe and by us for as long as tax and accounting law requires, even after
            an account is deleted.
          </li>
          <li>
            <strong>Your agreement to the renewal terms</strong> (when you agreed, and the words) is kept as a record for 3 years, or 1 year after your
            subscription ends, whichever is later, even after an account is deleted, because some state laws require it.
          </li>
          <li>
            Removing a child deletes their profile, schedule, rules, activity, records, Homeschool documents, school links and paired devices. You can delete your account yourself in Settings →
            Account security: after a short grace period (signing in before then keeps it) we erase it and your family’s information. Children you added
            to another parent’s family stay with that family.
          </li>
        </ul>

        <h2>Children’s privacy</h2>
        <p>
          ClassGuardian collects information about children only because their parent or guardian adds them and chooses what to supervise. By adding a
          child, the parent gives consent for ClassGuardian to collect the information described above to provide the service to them. A parent can at
          any time see everything we hold about their children, change it, export it (Settings → Account security → Export my data), stop further
          collection by unpairing devices or removing the child, and have it deleted. We don’t require a child to share more than the service needs, and
          we never use a child’s information for advertising or share it for anyone else’s purposes. A parent can contact us about their child’s
          information using the details below. A child’s legal name and date of birth are collected only after the family’s owner turns on state
          compliance and agrees to it, are deleted when it’s turned off, and a child’s records are shared outside the family only through an evaluator link the parent makes and can turn off.
        </p>

        <h2>Security</h2>
        <p>
          Connections to ClassGuardian are encrypted (HTTPS). Passwords are stored only as secure hashes, sensitive secrets are encrypted, sign-ins can be
          protected with two-factor or passkeys, and each paired device has its own credential that a parent can revoke at any time by unlinking it.
          Evaluator links use long random addresses that can’t be guessed, ask search engines not to index them, and show only what the parent chose. No
          system is perfectly secure; if a breach affects your information, we’ll tell you as the law requires.
        </p>

        <h2>Your choices and rights</h2>
        <ul>
          <li>See and edit your family’s information in the app at any time.</li>
          <li>Download a copy of everything in Settings → Account security → Export my data.</li>
          <li>Change school hours, website and app rules, and notifications whenever you like, including turning off the weekly summary email.</li>
          <li>Unlink a device, remove a child, or delete your account and all its data.</li>
          <li>
            Depending on where you live, you may have further rights over your information, such as to object to how it’s used. Contact us to use them;
            we won’t treat you differently for doing so.
          </li>
        </ul>

        <h2>Self-hosted servers</h2>
        <p>
          ClassGuardian can also be run on a family’s or organization’s own server. In that case the data is stored on that server and the person who
          runs it controls it.
        </p>

        <h2>Changes</h2>
        <p>
          If we change this policy, we’ll update the date at the top. If a change affects how we use information we already have, we’ll tell you by email
          or in the app before it takes effect.
        </p>

        <h2>Contact</h2>
        <p>
          Questions or requests about your data: email <a href="mailto:support@classguardian.app">support@classguardian.app</a>, call or text (803)
          887-0515, or use <strong>Help &amp; Feedback</strong> in the app.
        </p>
      </main>
      <footer className="landing-footer muted small">
        <Logo /> <span>Homeschool, organized. School time, focused.</span>
        <span className="landing-footer-links">
          <Link to="/terms" className="landing-footer-link">
            Terms of Service
          </Link>
        </span>
      </footer>
    </div>
  );
}
